import React, { useState, useEffect, useRef } from 'react';
import './DetailOffer.css'; // ⬅️ используем тот же CSS
import axios from 'axios';
import { Info, Check, X, Download, Pencil, FileSearch } from 'lucide-react';
import ComponentCard from '../component/ComponentCard';
import CatalogAssetIcons from './CatalogAssetIcons';
import CatalogRowPreview from './CatalogRowPreview';
import DocumentCharacteristicsButton from './DocumentCharacteristicsButton';
import useCatalogAssetAvailability from '../hooks/useCatalogAssetAvailability';

const DetailOrders = ({
    isOpen,
    selectedOrderFileId,
    onClose,
    numberOffer,
    highlightRowId = null,
    onOpenOriginal,
    catalogLinks = false
}) => {
    const [data, setData] = useState([]);
    const catalogAssets = useCatalogAssetAvailability(data, isOpen, catalogLinks);
    const [isLoading, setIsLoading] = useState(false);
    const [refreshLoading, setRefreshLoading] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [editingValue, setEditingValue] = useState('');

    const [selectedComponent, setSelectedComponent] = useState(null);
    const [componentInfo, setComponentInfo] = useState(null);
    const [infoLoading, setInfoLoading] = useState(false);
    const highlightedRowRef = useRef(null);

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    const [filters, setFilters] = useState({
        error: true,
        warning: true,
        changed: true,
        normal: true,
    });

    const counters = {
        error: data.filter(i => i.has_error === 1).length,
        warning: data.filter(i => i.has_error === 2).length,
        changed: data.filter(i => i.is_changed).length,
        normal: data.filter(
            i => i.has_error === 0 && !i.is_changed
        ).length,
    };
    const filteredData = data.filter(item => {
        if (item.has_error === 1 && !filters.error) return false;
        if (item.has_error === 2 && !filters.warning) return false;
        if (item.is_changed && !filters.changed) return false;

        const isNormal =
            item.has_error === 0 && !item.is_changed;

        if (isNormal && !filters.normal) return false;

        return true;
    });

    useEffect(() => {
        if (!isOpen || !highlightRowId || !data.length) return;
        requestAnimationFrame(() => highlightedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    }, [data, highlightRowId, isOpen]);

    const refreshComponentData = async () => {
        if (!selectedComponent) return;

        setRefreshLoading(true);

        try {
            await axios.post('/api/components/refresh-component', {
                part_number: selectedComponent,
            });

            const updated = await axios.post('/api/components/component-info', {
                part_number: selectedComponent,
            });

            setComponentInfo(updated.data?.component ?? null);
        } catch (error) {
            console.error('Ошибка обновления компонента:', error);
        } finally {
            setRefreshLoading(false);
        }
    };



    // === LOAD DATA ===
    useEffect(() => {
        if (!isOpen || !selectedOrderFileId) return;

        setIsLoading(true);

        const formData = new FormData();
        formData.append('orders', selectedOrderFileId);

        axios
            .post('/api/detailOrder', formData)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                setData(
                    list.map((item) => ({
                        ...item,
                        has_error: item.has_error ?? 0,
                        is_changed: item.changed ?? false,
                    }))
                );
            })
            .catch((err) => console.error('Ошибка:', err))
            .finally(() => setIsLoading(false));
    }, [isOpen, selectedOrderFileId]);

    // === SAVE PN ===
    const savePartNumber = async (row) => {
        try {
            await axios.post('/api/orders/update-partnumber', {
                id: row.id,
                new_partnumber: editingValue,
            });

            setData((prev) =>
                prev.map((item) =>
                    item.id === row.id
                        ? { ...item, name: editingValue, is_changed: true }
                        : item
                )
            );

            setEditingId(null);
            setEditingValue('');
        } catch (err) {
            console.error('Ошибка сохранения partnumber:', err);
        }
    };

    // === INFO ===
    const fetchComponentInfo = async (partNumber) => {
        if (!partNumber) return;

        setSelectedComponent(partNumber);
        setInfoLoading(true);
        setComponentInfo(null);

        try {
            const response = await axios.post('/api/components/component-info', {
                part_number: partNumber,
            });

            setComponentInfo(response.data?.component);
        } catch (error) {
            console.error('Ошибка загрузки компонента:', error);
            setComponentInfo({ error: 'Не удалось загрузить данные' });
        } finally {
            setInfoLoading(false);
        }
    };




    if (!isOpen) return null;

    return (
        <>
            {/* ================= MAIN MODAL ================= */}
            <div className="modal_backdrop" onClick={onClose}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modalContent">

                        {/* ===== HEADER ===== */}
                        <div className="modalHeader">

                            <h2>Детализация запроса {numberOffer}</h2>
                            <DocumentCharacteristicsButton key={selectedOrderFileId} kind="order" documentId={selectedOrderFileId} disabled={isLoading || !data.length} />
                            {catalogAssets.error && <span role="status">Не удалось проверить изображения и PDF. <button type="button" onClick={catalogAssets.retry}>Повторить</button></span>}

                            <div className="legend">
                                <label className="legendItem">
                                    <input
                                        type="checkbox"
                                        checked={filters.error}
                                        onChange={() =>
                                            setFilters(f => ({ ...f, error: !f.error }))
                                        }
                                    />
                                    <span className="legendRed" />
                                    Нет информации ({counters.error})
                                </label>

                                <label className="legendItem">
                                    <input
                                        type="checkbox"
                                        checked={filters.warning}
                                        onChange={() =>
                                            setFilters(f => ({ ...f, warning: !f.warning }))
                                        }
                                    />
                                    <span className="legendYellow" />
                                    Не загружена ({counters.warning})
                                </label>

                                <label className="legendItem">
                                    <input
                                        type="checkbox"
                                        checked={filters.changed}
                                        onChange={() =>
                                            setFilters(f => ({ ...f, changed: !f.changed }))
                                        }
                                    />
                                    <span className="legendBlue" />
                                    Изменено ({counters.changed})
                                </label>

                                <label className="legendItem">
                                    <input
                                        type="checkbox"
                                        checked={filters.normal}
                                        onChange={() =>
                                            setFilters(f => ({ ...f, normal: !f.normal }))
                                        }
                                    />
                                    <span className="legendGray" />
                                    Обработанные ({counters.normal})
                                </label>
                            </div>


                            <div className="headerActions">
                                {onOpenOriginal && (
                                    <button
                                        className="sourceBtn"
                                        onClick={onOpenOriginal}
                                        title="Посмотреть исходный документ"
                                    >
                                        <FileSearch size={16} />
                                        Исходник
                                    </button>
                                )}
                                <button
                                    data-modal-close
                                    className="closeBtn"
                                    onClick={onClose}
                                    title="Закрыть"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* ===== BODY ===== */}
                        <div className="modalBody">
                            {isLoading ? (
                                <div className="loadingContainer">
                                    <div className="spinner" />
                                    Загрузка данных...
                                </div>
                            ) : (
                                <>
                                    {/* ===== DESKTOP TABLE ===== */}
                                    {!isMobile && (
                                        <div className="tableContainer">
                                            <table className="table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 56 }}>Info</th>
                                                        <th>Part Number</th>
                                                        <th>Manufacturer</th>
                                                        <th>Quantity</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {
                                                        filteredData.map(item => (
                                                            <tr
                                                                key={item.id}
                                                                ref={String(item.id) === String(highlightRowId) ? highlightedRowRef : null}
                                                                className={`${item.is_changed ? 'changedRow' : ''} ${String(item.id) === String(highlightRowId) ? 'selectedSearchRow' : ''}`}
                                                            >
                                                                {/* INFO COLUMN */}
                                                                <td>
                                                                    <button
                                                                        className="infoButton"
                                                                        onClick={() => fetchComponentInfo(item.name)}
                                                                        title={
                                                                            item.has_error === 2
                                                                                ? 'Загрузить информацию по компоненту'
                                                                                : 'Показать информацию по компоненту'
                                                                        }
                                                                    >
                                                                        {item.has_error === 2 ? (
                                                                            <Download size={16} />
                                                                        ) : (
                                                                            <Info size={16} />
                                                                        )}
                                                                    </button>
                                                                </td>

                                                                {/* PART NUMBER */}
                                                                <td
                                                                    className={[
                                                                        'partNumberCell',
                                                                        item.has_error === 1 ? 'cellError' : '',
                                                                        item.has_error === 2 ? 'cellWarning' : '',
                                                                    ].filter(Boolean).join(' ')}
                                                                >
                                                                    {editingId === item.id ? (
                                                                        <div className="editWrapper">
                                                                            <input
                                                                                className="editInput"
                                                                                value={editingValue}
                                                                                onChange={(e) => setEditingValue(e.target.value)}
                                                                                autoFocus
                                                                            />

                                                                            <button
                                                                                className="iconBtn"
                                                                                onClick={() => savePartNumber(item)}
                                                                                title="Сохранить"
                                                                            >
                                                                                <Check className="iconCheck" />
                                                                            </button>

                                                                            <button
                                                                                className="iconBtn"
                                                                                onClick={() => setEditingId(null)}
                                                                                title="Отмена"
                                                                            >
                                                                                <X className="iconClose" />
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <span
                                                                            className="editable"
                                                                            title="Нажмите, чтобы изменить"
                                                                            onClick={() => {
                                                                                setEditingId(item.id);
                                                                                setEditingValue(item.name ?? '');
                                                                            }}
                                                                        >
                                                                            {item.name}
                                                                            <Pencil className="editHintIcon" size={13} aria-hidden="true" />
                                                                        </span>
                                                                    )}
                                                                    {catalogLinks && <CatalogRowPreview partNumber={item.name} manufacturer={item.wrk} materials={catalogAssets.materials[item.name?.trim()]} />}{!catalogLinks && <CatalogAssetIcons key={item.name} partNumber={item.name} materials={catalogAssets.materials[item.name?.trim()]} />}
                                                                </td>

                                                                {/* MANUFACTURER */}
                                                                <td>{item.wrk}</td>

                                                                {/* QUANTITY */}
                                                                <td>{item.cnt}</td>
                                                            </tr>

                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* ===== MOBILE CARDS ===== */}
                                    {isMobile && (
                                        <div className="mobileCards">
                                            {
                                                filteredData.map(item => (
                                                    <div
                                                        key={item.id}
                                                        ref={String(item.id) === String(highlightRowId) ? highlightedRowRef : null}
                                                        className={`card ${item.is_changed ? 'changedRow' : ''} ${String(item.id) === String(highlightRowId) ? 'selectedSearchRow' : ''}`}
                                                    >
                                                        <div className="cardRow">
                                                            <span className="cardLabel">Part Number:</span>
                                                            <span>{item.name}{catalogLinks && <CatalogRowPreview partNumber={item.name} manufacturer={item.wrk} materials={catalogAssets.materials[item.name?.trim()]} />}{!catalogLinks && <CatalogAssetIcons key={item.name} partNumber={item.name} materials={catalogAssets.materials[item.name?.trim()]} />}</span>
                                                        </div>

                                                        <div className="cardRow">
                                                            <span className="cardLabel">Manufacturer:</span>
                                                            <span>{item.wrk}</span>
                                                        </div>

                                                        <div className="cardRow">
                                                            <span className="cardLabel">Quantity:</span>
                                                            <span>{item.cnt}</span>
                                                        </div>

                                                        <button
                                                            className="cardInfoBtn"
                                                            onClick={() => fetchComponentInfo(item.name)}
                                                        >
                                                            Показать информацию
                                                        </button>

                                                        <div className="cardButtons">
                                                            {editingId === item.id ? (
                                                                <>
                                                                    <button
                                                                        className="iconBtn"
                                                                        onClick={() => savePartNumber(item)}
                                                                    >
                                                                        <Check className="iconCheck" />
                                                                    </button>
                                                                    <button
                                                                        className="iconBtn"
                                                                        onClick={() => setEditingId(null)}
                                                                    >
                                                                        <X className="iconClose" />
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <button
                                                                    className="iconBtn"
                                                                    onClick={() => {
                                                                        setEditingId(item.id);
                                                                        setEditingValue(item.name);
                                                                    }}
                                                                >
                                                                    ✎
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ================= INFO MODAL ================= */}
            {selectedComponent && (
                <div
                    className="modal_backdrop"
                    onClick={() => setSelectedComponent(null)}
                >
                    <div
                        className="infoModal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* ===== HEADER ===== */}
                        <div className="infoModalHeader">
                            <h2 className="infoTitle">
                                Информация о компоненте: {selectedComponent}
                            </h2>

                            <button
                                data-modal-close
                                className="infoCloseBtn"
                                onClick={() => setSelectedComponent(null)}
                                title="Закрыть"
                                aria-label="Закрыть"
                            >
                                ✕
                            </button>
                        </div>

                        {/* ===== BODY ===== */}
                        {infoLoading ? (
                            <div className="loadingContainer">
                                <div className="spinner" />
                                Загрузка...
                            </div>
                        ) : (
                            (
                                <ComponentCard
                                    component={{ ...componentInfo, part_number: componentInfo?.part_number || selectedComponent }}
                                    onRefresh={refreshComponentData}
                                    refreshLoading={refreshLoading}
                                />
                            )
                        )}
                    </div>
                </div>
            )}

        </>
    );
};

export default DetailOrders;
